// constants
import { ROW_HEIGHT_PER_DUTY } from '../RosterPlan.consts';

// types
import type { OpenRequestResponse } from 'RestAPIs/roster/open-requests/types';

// utils
import { toDateString } from 'utils/date';

/**
 * Only the dates are read, so the parameter is the shape this needs rather than the whole group:
 * MUI hands `getRowHeight` a widened `GridValidRowModel`, and an object type whose properties are
 * all optional accepts that without a cast, while still type-checking the fields it does read.
 */
type OpenRequestRowHeightInput = { days?: OpenRequestResponse[] };

/**
 * How tall one open-requests row has to be: as many duty slots as the busiest *day* in the group.
 *
 * A group's `days` is a flat list of requests, not one entry per day, so they have to be bucketed
 * by date first — three requests on one day stack into three slots, while three requests on three
 * days each need only one.
 *
 * An unparseable date is skipped rather than bucketed. `date` is typed non-optional, so this is
 * defensive, but `format` throws on an invalid date where the `dayjs` call this replaces returned
 * the string "Invalid Date" — and this runs inside MUI's render, so one malformed request would
 * take the whole grid down instead of mis-placing one row.
 */
export const getOpenRequestRowHeight = (group?: OpenRequestRowHeightInput) => {
  const requests = group?.days ?? [];

  const countPerDate = requests.reduce<Record<string, number>>(
    (counts, request) => {
      const date = new Date(request?.date);
      if (Number.isNaN(date.getTime())) return counts;

      const key = toDateString(date);
      counts[key] = (counts[key] ?? 0) + 1;

      return counts;
    },
    {}
  );

  return ROW_HEIGHT_PER_DUTY * Math.max(...Object.values(countPerDate), 1);
};
